import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Minus, Truck, Package as PackageIcon, ShoppingBag, Search, FileDown, UserPlus, LogIn, CreditCard, FileText, DollarSign, ArrowRight, Check } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import StickyMobileCTA from "./StickyMobileCTA";
import BackToTop from "./BackToTop";
import { Reveal } from "./Reveal";
import { BRAND } from "../data/content";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import OptionPickerModal from "./OptionPickerModal";
import PaymentMethodGate from "./PaymentMethodGate";
import ItemQuoteModal from "./ItemQuoteModal";
import { api } from "../lib/api";

/**
 * Private (retail) menu page.
 * Full catalog from GoDaddy product export — 61 items across 13 categories.
 * Every item card has: image, name, description, price ("Half Pan"), quantity
 * stepper, and Add to Cart. Agency users see institutional prices inline (no
 * extra badge per user request).
 */

const slugifyCat = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

// Categories whose items are NOT priced by the half pan — hide the
// "Half Pan" badge and the page-level "priced per half pan" disclaimer.
const NON_PAN_CATEGORIES = new Set([
  "Dessert",
  "Beverages",
  "Add-ons",
  "Merch",
  "Basic Dinnerware",
  "Breakfast & Brunch",
]);

const QuoteOnlyFooter = ({ item, onQuote }) => (
  <div className="mt-auto pt-4 flex justify-end">
    <button
      type="button"
      onClick={() => onQuote(item)}
      data-testid={`menu-request-quote-${item.id}`}
      className="inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-4 py-2 text-[10.5px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
    >
      Request Quote
      <ArrowRight className="h-3 w-3" />
    </button>
  </div>
);

const ItemCard = ({ item, onAdd, onQuote, isAgency }) => {
  const quoteOnly = Boolean(item.quote_only);
  const [qty, setQty] = useState(1);
  const dec = () => setQty((q) => Math.max(1, q - 1));
  const inc = () => setQty((q) => Math.min(50, q + 1));
  const hasOptions = Array.isArray(item.options) && item.options.length > 0;
  const showHalfPan = !NON_PAN_CATEGORIES.has(item.category);
  // Per-person packages (Continental Upgrade / Southern / Executive) carry
  // `unit_label: "person"` so the tile shows "$14.95 / person" instead of
  // just "$14.95" — makes it obvious the customer enters guest count.
  const unitSuffix = item.unit_label ? ` / ${item.unit_label}` : "";
  const priceLabel =
    item.price_max && item.price_max !== item.price
      ? `$${item.price.toFixed(2)} – $${item.price_max.toFixed(2)}${unitSuffix}`
      : `$${item.price.toFixed(2)}${unitSuffix}`;

  const handleAdd = () => {
    // For items with sauce/variant choices, parent handles the modal.
    if (hasOptions) {
      onAdd(item, null, true);
      return;
    }
    onAdd(item, qty, false);
  };

  return (
    <article
      data-testid={`menu-item-${item.id}`}
      className="group flex flex-col h-full rounded-2xl bg-white border border-black/5 overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-22px_rgba(17,17,17,0.18)]"
    >
      {item.image ? (
        <div className="relative h-44 sm:h-48 bg-brand-cream overflow-hidden">
          <img
            src={item.image}
            alt={item.name}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          {!quoteOnly && <span
            data-testid={`menu-price-${item.id}`}
            className="absolute top-3 right-3 rounded-full bg-white text-brand-charcoal px-2.5 py-1 text-[12px] font-black tracking-tight tabular-nums shadow-[0_4px_12px_-4px_rgba(0,0,0,0.25)]"
          >
            {priceLabel}
          </span>}
        </div>
      ) : (
        <div className="relative h-32 sm:h-36 bg-brand-cream/60 flex items-center justify-center">
          <ShoppingBag className="h-6 w-6 text-brand-charcoal/30" />
          {!quoteOnly && <span
            data-testid={`menu-price-${item.id}`}
            className="absolute top-3 right-3 rounded-full bg-white border border-black/10 text-brand-charcoal px-2.5 py-1 text-[12px] font-black tracking-tight tabular-nums"
          >
            {priceLabel}
          </span>}
        </div>
      )}

      <div className="flex flex-col flex-1 p-4 sm:p-5">
        <h3 className="font-display font-bold text-[15px] sm:text-[16px] text-brand-charcoal tracking-tight leading-tight">
          {item.name}
        </h3>
        {item.description && (
          <p className="mt-2 text-[12.5px] text-brand-charcoal/65 leading-snug line-clamp-3">
            {item.description}
          </p>
        )}

        {quoteOnly ? <QuoteOnlyFooter item={item} onQuote={onQuote} /> : (
        <div className="mt-auto pt-4 flex items-center justify-between gap-2">
          {showHalfPan ? (
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
              Half Pan
            </p>
          ) : item.unit_label ? (
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-brand-teal">
              Enter guest count
            </p>
          ) : (
            <span aria-hidden="true" />
          )}

          <div className="flex items-center gap-2">
            {!hasOptions && (
              <div
                className="inline-flex items-center gap-1 rounded-full bg-brand-cream border border-black/5 p-1"
                data-testid={`menu-qty-${item.id}`}
              >
                <button
                  type="button"
                  onClick={dec}
                  data-testid={`menu-qty-dec-${item.id}`}
                  aria-label={`Decrease quantity of ${item.name}`}
                  className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-white text-brand-charcoal disabled:opacity-40"
                  disabled={qty <= 1}
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span
                  data-testid={`menu-qty-value-${item.id}`}
                  className="min-w-[18px] text-center text-[13px] font-bold tabular-nums"
                >
                  {qty}
                </span>
                <button
                  type="button"
                  onClick={inc}
                  data-testid={`menu-qty-inc-${item.id}`}
                  aria-label={`Increase quantity of ${item.name}`}
                  className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-white text-brand-charcoal"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={handleAdd}
              data-testid={`menu-add-${item.id}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
            >
              <Plus className="h-3 w-3" />
              {hasOptions ? `Choose ${item.options[0].label}` : "Add"}
            </button>
          </div>
        </div>
        )}
      </div>
    </article>
  );
};

// Package showcase card — larger, image-forward card used inside the
// "Packages" category tab. Packages are curated bundles (Breakfast,
// Wings & Sliders, Executive, etc.) that don't have a fixed catalog
// price, so the CTA routes to /quote?package=<id> for a custom quote.
const PackageCard = ({ pkg }) => {
  const preview = Array.isArray(pkg.includes) ? pkg.includes.slice(0, 4) : [];
  const remainder = Math.max(0, (pkg.includes?.length || 0) - preview.length);
  return (
    <article
      data-testid={`menu-package-card-${pkg.id}`}
      className="group rounded-2xl bg-white border border-black/5 overflow-hidden flex flex-col hover:border-brand-teal/40 hover:shadow-[0_18px_36px_-20px_rgba(0,166,178,0.35)] transition-all"
    >
      {pkg.image && (
        <div className="aspect-[16/10] bg-brand-cream/60 overflow-hidden">
          <img
            src={pkg.image}
            alt={pkg.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
      )}
      <div className="p-4 sm:p-5 flex-1 flex flex-col">
        <p className="text-[10px] font-black uppercase tracking-[0.14em] text-brand-teal">
          Curated Package
        </p>
        <h3 className="mt-1.5 font-display font-black text-[17px] text-brand-charcoal tracking-tight leading-tight">
          {pkg.name}
        </h3>
        {pkg.description && (
          <p className="mt-2 text-[12.5px] text-brand-charcoal/70 leading-relaxed line-clamp-3">
            {pkg.description}
          </p>
        )}
        {preview.length > 0 && (
          <ul className="mt-3 space-y-1">
            {preview.map((line) => (
              <li key={line} className="flex items-start gap-1.5 text-[11.5px] text-brand-charcoal/75 leading-snug">
                <Check className="h-3 w-3 shrink-0 mt-[3px] text-brand-green" />
                <span className="line-clamp-1">{line}</span>
              </li>
            ))}
            {remainder > 0 && (
              <li className="text-[11px] font-bold text-brand-charcoal/50 pl-[18px]">
                +{remainder} more
              </li>
            )}
          </ul>
        )}
        {pkg.best_for && (
          <p className="mt-3 text-[11px] text-brand-charcoal/55 italic leading-snug">
            Best for: {pkg.best_for}
          </p>
        )}
        <div className="mt-4 pt-4 border-t border-black/5 flex items-center justify-between gap-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/55">
            Custom quote
          </span>
          <Link
            to={`/quote?package=${encodeURIComponent(pkg.id)}`}
            data-testid={`menu-package-quote-${pkg.id}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-4 py-2 text-[10.5px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
          >
            Request quote
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </article>
  );
};

export const PrivateMenuPage = () => {
  const { menu, addItem, deliveryMinimum, paymentChoice, paymentRequest } = useCart();
  const { user } = useAuth();
  const isAgency = user && (user.role === "agency" || user.role === "admin");
  const [query, setQuery] = useState("");
  const [pickerItem, setPickerItem] = useState(null);
  const [quoteItem, setQuoteItem] = useState(null);
  const [packages, setPackages] = useState([]);
  const [gateManualOpen, setGateManualOpen] = useState(false);
  const requestPending = user?.role === "agency" && paymentRequest?.status === "pending";

  // Handler passed down to ItemCard. When the item has options, open modal;
  // otherwise add directly.
  const handleAddFromCard = (item, qty, needsPicker) => {
    if (needsPicker) {
      setPickerItem(item);
      return;
    }
    addItem(item.id, qty || 1);
  };

  const handleModalConfirm = ({ perPanSelections }) => {
    if (!pickerItem) return;
    // Add one cart line per pan. CartContext merges lines with identical
    // (package_id + selected_options), so picking the same modifier twice
    // still collapses into a single line with the correct quantity.
    for (const panSel of perPanSelections || [{}]) {
      addItem(pickerItem.id, 1, panSel);
    }
    setPickerItem(null);
  };

  // Load curated packages (home_packages collection) once so they appear
  // as a dedicated "Packages" tab in the menu category strip. Packages
  // route to /quote for a custom bid instead of add-to-cart.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/packages");
        if (!cancelled) setPackages(data.items || []);
      } catch {
        if (!cancelled) setPackages([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const { byCategory, categories } = useMemo(() => {
    const map = {};
    for (const item of menu.items) {
      const cat = item.category || "Miscellaneous";
      (map[cat] ||= []).push(item);
    }
    const ordered = (menu.categories_order || []).filter((c) => map[c]);
    const extras = Object.keys(map).filter((c) => !ordered.includes(c)).sort();
    // Prepend a synthetic "Packages" category when we have curated bundles
    // to show. The renderer switches on this name to draw PackageCards
    // instead of standard ItemCards. `byCategory["Packages"]` holds the
    // list of package objects rather than menu items.
    const cats = [...ordered, ...extras];
    if (packages.length > 0) {
      map.Packages = packages;
      cats.unshift("Packages");
    }
    return { byCategory: map, categories: cats };
  }, [menu, packages]);

  const [activeCat, setActiveCat] = useState(categories[0] || "Chicken");
  if (categories.length && !categories.includes(activeCat)) {
    setTimeout(() => setActiveCat(categories[0]), 0);
  }

  const trimmedQuery = query.trim().toLowerCase();
  const isSearching = trimmedQuery.length >= 2;
  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    return menu.items.filter((it) => {
      const hay = `${it.name} ${it.description || ""} ${it.category || ""}`.toLowerCase();
      return hay.includes(trimmedQuery);
    });
  }, [menu.items, trimmedQuery, isSearching]);

  const visibleItems = isSearching ? searchResults : (byCategory[activeCat] || []);

  return (
    <div className="App font-sans bg-white text-brand-charcoal antialiased">
      <Header />
      {gateManualOpen && (
        <PaymentMethodGate onClose={() => setGateManualOpen(false)} />
      )}
      <main>
        {/* HERO */}
        <section className="pt-24 sm:pt-28 lg:pt-32 pb-10 sm:pb-12 bg-brand-cream">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10">
            <Reveal>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Link
                  to="/"
                  data-testid="back-to-home-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-white border border-black/10 px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal hover:bg-brand-charcoal hover:text-white transition-colors"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back to Home
                </Link>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/menu/pdf`}
                  download="fresh-fast-foods-menu.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="menu-download-pdf-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.14em] hover:bg-brand-teal transition-colors shadow-[0_8px_22px_-10px_rgba(17,17,17,0.45)]"
                >
                  <FileDown className="h-3.5 w-3.5" />
                  Download Menu PDF
                </a>
              </div>
            </Reveal>
            <Reveal delay={1}>
              <p className="mt-6 text-[12px] font-bold uppercase tracking-[0.22em] text-brand-gold-dark">
                {isAgency
                  ? paymentChoice === "po"
                    ? "Agency Menu · Agency Pricing"
                    : user.role === "admin" ? "Agency Menu · Retail Pricing (admin preview)" : "Agency Menu · Retail Pricing (approved)"
                  : "Private Catering · Retail Pricing"}
              </p>
              {/* Payment-choice pill (agency only). Shows the picked tier and
                  reopens the gate so the user can switch mid-session. */}
              {isAgency && paymentChoice && (
                <button
                  type="button"
                  onClick={() => setGateManualOpen(true)}
                  data-testid="payment-choice-pill"
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-white border border-brand-charcoal/15 px-3.5 py-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal hover:border-brand-teal hover:text-brand-teal transition-colors"
                >
                  {paymentChoice === "po" && <FileText className="h-3 w-3" />}
                  {paymentChoice === "cc" && <CreditCard className="h-3 w-3" />}
                  {paymentChoice === "cash" && <DollarSign className="h-3 w-3" />}
                  Paying by {paymentChoice === "po" ? "Purchase Order" : paymentChoice === "cc" ? "Credit Card" : "Cash"}
                  {requestPending && <span data-testid="payment-pill-pending" className="text-brand-gold-dark"> · Approval pending</span>} · Change
                </button>
              )}
            </Reveal>
            <Reveal delay={2}>
              <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-charcoal leading-[1.05]">
                The Menu<span className="text-brand-gold">.</span>
              </h1>
            </Reveal>
            <Reveal delay={3}>
              <p className="mt-4 max-w-2xl text-[15px] lg:text-[17px] text-brand-charcoal/70 leading-relaxed">
                Build your event from <strong>{menu.items.length}</strong> items across our full catering menu.
                Delivery requires a <strong>${deliveryMinimum}</strong> minimum (waived for pickup).
                Most savory items are priced per half pan — desserts, beverages, add-ons and merch are priced individually.
              </p>
            </Reveal>

            <Reveal delay={4}>
              <div className="mt-6 grid sm:grid-cols-2 gap-3 max-w-2xl text-[12.5px]">
                <div className="flex items-center gap-2.5 rounded-2xl bg-white border border-black/5 px-4 py-3">
                  <Truck className="h-4 w-4 text-brand-teal" />
                  <p><strong className="text-brand-charcoal">Delivery</strong> · ZIP-based fee · ${deliveryMinimum} min</p>
                </div>
                <div className="flex items-center gap-2.5 rounded-2xl bg-white border border-black/5 px-4 py-3">
                  <PackageIcon className="h-4 w-4 text-brand-teal" />
                  <p><strong className="text-brand-charcoal">Pickup</strong> · {BRAND.pickupAddress.split(",")[0]}</p>
                </div>
              </div>
            </Reveal>

            {!user && (
              <Reveal delay={5}>
                <div
                  data-testid="menu-create-account-card"
                  className="mt-6 max-w-2xl rounded-2xl bg-white border border-black/5 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4"
                >
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-teal/12 text-brand-teal">
                    <UserPlus className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-black text-[15.5px] text-brand-charcoal tracking-tight leading-tight">
                      Create an account to checkout faster
                    </p>
                    <p className="mt-1 text-[12.5px] text-brand-charcoal/65 leading-snug">
                      Save your contact details, track order history, and skip
                      retyping for repeat events. Browse without one if you prefer.
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Link
                      to="/register"
                      data-testid="menu-create-account-btn"
                      className="inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-all whitespace-nowrap"
                    >
                      <UserPlus className="h-3.5 w-3.5" />
                      Create Account
                    </Link>
                    <Link
                      to="/login"
                      data-testid="menu-sign-in-btn"
                      className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-charcoal/15 text-brand-charcoal px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] hover:border-brand-teal hover:text-brand-teal transition-all whitespace-nowrap"
                    >
                      <LogIn className="h-3.5 w-3.5" />
                      Sign In
                    </Link>
                  </div>
                </div>
              </Reveal>
            )}
          </div>
        </section>

        {/* MENU BODY */}
        <section className="py-10 sm:py-14 bg-brand-gray/40">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10">
            {/* Search + tabs */}
            <div className="space-y-4 mb-6">
              <div className="relative max-w-md">
                <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-brand-charcoal/40 pointer-events-none" />
                <Input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search the menu…"
                  aria-label="Search menu"
                  data-testid="menu-search"
                  className="h-11 pl-10 rounded-full bg-white border-black/10"
                />
              </div>

              {!isSearching && (
                <div
                  data-testid="menu-category-tabs"
                  className="-mx-1 px-1 flex gap-2 overflow-x-auto md:overflow-visible md:flex-wrap no-scrollbar snap-x snap-mandatory md:snap-none"
                >
                  {categories.map((cat) => {
                    const active = cat === activeCat;
                    const count = byCategory[cat]?.length || 0;
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setActiveCat(cat)}
                        data-testid={`menu-tab-${slugifyCat(cat)}`}
                        className={`snap-start whitespace-nowrap inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11.5px] font-bold uppercase tracking-[0.12em] transition-colors ${
                          active
                            ? "bg-brand-charcoal text-white"
                            : "bg-white border border-black/10 text-brand-charcoal/70 hover:bg-brand-cream"
                        }`}
                      >
                        {cat}
                        <span className={`rounded-full px-1.5 py-0.5 text-[9.5px] ${active ? "bg-white/15 text-white" : "bg-brand-charcoal/5 text-brand-charcoal/65"}`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Items */}
            <div className="mb-4 flex items-baseline justify-between">
              <h2 className="font-display font-black text-2xl sm:text-3xl text-brand-charcoal tracking-tight">
                {isSearching ? `Search results` : activeCat}
              </h2>
              <p className="text-[12px] text-brand-charcoal/55">
                {isSearching
                  ? `${visibleItems.length} match${visibleItems.length === 1 ? "" : "es"}`
                  : `${visibleItems.length} item${visibleItems.length === 1 ? "" : "s"}`}
              </p>
            </div>

            <div
              data-testid={isSearching ? "menu-search-results" : `menu-category-${slugifyCat(activeCat)}`}
              className={
                activeCat === "Packages" && !isSearching
                  ? "grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
                  : "grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
              }
            >
              {activeCat === "Packages" && !isSearching
                ? visibleItems.map((pkg) => (
                    <PackageCard key={pkg.id} pkg={pkg} />
                  ))
                : visibleItems.map((it) => (
                    <ItemCard key={it.id} item={it} onAdd={handleAddFromCard} onQuote={setQuoteItem} isAgency={isAgency} />
                  ))}
              {visibleItems.length === 0 && (
                <p className="col-span-full text-center text-[14px] text-brand-charcoal/55 py-12">
                  {isSearching ? "No items matched your search." : "Nothing in this category yet."}
                </p>
              )}
            </div>

            <div className="mt-12 rounded-3xl bg-white border border-black/5 p-6 sm:p-8 text-center">
              <ShoppingBag className="h-6 w-6 mx-auto text-brand-charcoal/40" />
              <p className="mt-3 font-display font-bold text-[18px] text-brand-charcoal tracking-tight">
                Ready to check out?
              </p>
              <p className="mt-2 text-[13.5px] text-brand-charcoal/65 leading-relaxed max-w-md mx-auto">
                Card payments for private orders. Agency PO orders accepted with login.
              </p>
              <Link
                to="/cart"
                data-testid="menu-go-to-cart-btn"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
              >
                Go to Cart
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <StickyMobileCTA />
      <BackToTop />
      <ItemQuoteModal item={quoteItem} onClose={() => setQuoteItem(null)} />
      <OptionPickerModal
        item={pickerItem}
        isOpen={Boolean(pickerItem)}
        onClose={() => setPickerItem(null)}
        onConfirm={handleModalConfirm}
        perPanEnabled
      />
    </div>
  );
};

export default PrivateMenuPage;
